import React, { FC, SyntheticEvent, useState } from 'react';

import { FormattedMessage, IntlShape } from 'react-intl';

import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { Button, ButtonType } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import CommonMessages from '~/core/commonMessages';
import { useIsWebview } from '~/core/hooks/useIsWebview';

import { ScheduleViewClassName } from '../../../../enums';

import { PrintFrame } from './styles';

type Props = {
    isMedium: boolean;
    isCalendarView: boolean;
    intl: IntlShape;
};

const BEFORE_UNLOAD_EVENT = 'beforeunload';
const AFTER_PRINT_EVENT = 'afterprint';

const PrintButton: FC<Props> = ({ isMedium, isCalendarView, intl }) => {
    const isWebview = useIsWebview();

    const [isPrinting, setIsPrinting] = useState<boolean>(false);

    const onClick = () => {
        // In order to print just the calendar and not the additional d3 styling, we open a webview version of the page
        // and then print
        if (isWebview) {
            // If we're already on the webview of a page, great! We can go ahead and print
            window.print();
        } else {
            // If we aren't, we open a webview version of the page in an iframe by setting isPrinting to true
            setIsPrinting(true);
        }
    };

    const onHiddenFrameLoaded = (e: SyntheticEvent<HTMLIFrameElement, Event>) => {
        const contentWindow = e.currentTarget.contentWindow;

        if (contentWindow) {
            // after we print or if the iframe is unloaded for some reason we set isPrinting to false to destroy the
            // iframe. These events don't exist on the react iframe component, so we have to add the event listeners
            // manually.
            contentWindow.addEventListener(BEFORE_UNLOAD_EVENT, () => setIsPrinting(false));
            contentWindow.addEventListener(AFTER_PRINT_EVENT, () => setIsPrinting(false));

            // We add an extra setTimeout to prevent a blank page from being printed right away
            const interval = setInterval(() => {
                // Here we check in intervals to make sure that the calendar has actually rendered, once it has,
                // we print and exit the interval loop
                if (contentWindow && contentWindow.document.getElementById(ScheduleViewClassName.CALENDAR)) {
                    contentWindow.print();
                    clearInterval(interval);
                }
            }, 500);
        }
    };

    return (
        isCalendarView && (
            <Button
                className="print-button"
                onClick={onClick}
                variant={ButtonType.Secondary}
                aria-label={intl.formatMessage(CommonMessages.ariaLabels.buttons.print)}
            >
                {!isMedium && <FormattedMessage {...CommonMessages.ariaLabels.buttons.print} />}
                <PrintOutlinedIcon />
                {isPrinting && (
                    <LoadingDisplay isLoading>
                        <PrintFrame src={`${window.location.href}?wv=true`} onLoad={onHiddenFrameLoaded} />
                    </LoadingDisplay>
                )}
            </Button>
        )
    );
};

export default PrintButton;
